import { useState, type VoidFunctionComponent } from 'react';

import { MANUAL_DEPENDENCIES_TEST_VALIDITY } from '../../../../shared/featureFlags/flagNames';
import {
  commonLinuxArchs,
  FETCHER_DROPDOWN_OPTIONS,
  isLinuxFetcher,
  linuxDistrosByFetcher,
  type ThirdPartyFetcher,
} from '../../../../shared/fetchers';
import Locator from '../../../../shared/Locator';
import { isFilled } from '../../../../shared/utils/isFilled';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { selectOptionFactory } from '../../../utils';
import { LabeledSelect, LabeledTextInput } from '../../generic';
import Button, { LoaderButton } from '../../generic/Button';
import type { ManuallyAddedDependency, ManuallyAddedDependencyRequest } from '../../../../typings/api';

import useCheckRevision from './hooks/useCheckRevision';

import styles from './SearchDependency.module.less';

const COMMON_LINUX_ARCH_OPTIONS = commonLinuxArchs.map((linuxArch) => selectOptionFactory(linuxArch));

export type Props = {
  /**
   * Whether the dependency is being added as a vendored dependency.
   */
  isVendored?: boolean;
  /**
   * Locator of the existing dependency when overwriting.
   */
  overwriteDependencyLocator?: string;

  /**
   * Callback to save the dependency.
   * NOTE: The return value here is typed as "unknown" because React Query's mutateAsync
   * returns whatever the mutation function returns but we don't care about it.
   */
  saveDependency?: (params: ManuallyAddedDependencyRequest) => Promise<unknown>;
  /**
   * Whether the dependency is currently being saved.
   */
  isSaving?: boolean;

  /**
   * Callback when the drawer is closed.
   */
  onClose?: () => void;
};

const SearchDependency: VoidFunctionComponent<Props> = ({
  isVendored,
  overwriteDependencyLocator,
  saveDependency,
  isSaving,
  onClose,
}) => {
  const [filePath, setFilePath] = useState<string>('');
  const [packageManager, setPackageManager] = useState<ThirdPartyFetcher | undefined>(undefined);
  const [name, setName] = useState<string>('');
  const [version, setVersion] = useState<string>('');

  const [distro, setDistro] = useState<string | undefined>(undefined);
  const [release, setRelease] = useState<string>('');
  const [arch, setArch] = useState<string | undefined>(undefined);

  const isSearchingForLinuxPackage = packageManager && isLinuxFetcher(packageManager);

  const canSaveVendored = isVendored ? isFilled(filePath) : true;
  const canSaveLinux = isSearchingForLinuxPackage ? !!distro && isFilled(release) && !!arch : true;
  const canSave = !!packageManager && isFilled(name) && isFilled(version) && canSaveVendored && canSaveLinux;

  const {
    mutate: checkRevision,
    isPending: isCheckingRevision,
    data: checkRevisionResponse,
    error: checkRevisionError,
  } = useCheckRevision();

  const isManualDependenciesTestValidityEnabled = useIsFeatureEnabled(MANUAL_DEPENDENCIES_TEST_VALIDITY);
  const shouldUseTestWorkflow = isManualDependenciesTestValidityEnabled && packageManager && packageManager !== 'git';
  const [hasTested, setHasTested] = useState(false);

  const isTestingOrSaving = isSaving || (shouldUseTestWorkflow && isCheckingRevision);

  const onTest = async () => {
    // NOTE: This will only ever be called if canSave is true but TS doesn't know that.
    if (!canSave) {
      return;
    }

    const locatorParts = {
      fetcher: packageManager,
      package: name,
      revision: version,
    };

    if (distro && release && arch) {
      locatorParts.package = `${name}#${distro}#${release}`;
      locatorParts.revision = `${arch}#${version}`;
    }

    const locator = new Locator(locatorParts);

    setHasTested(true);
    checkRevision(locator.toString());
  };

  const onSave = async () => {
    // NOTE: This will only ever be called if canSave is true but TS doesn't know that.
    if (!canSave) {
      return;
    }

    const newDependency: ManuallyAddedDependency = {
      kind: 'locator',
      fetcher: packageManager,
      packageName: name.trim(),
      revision: version.trim(),
      vendoredPath: filePath.trim() || undefined,
      distro: distro || undefined,
      distroRelease: release.trim() || undefined,
      arch: arch || undefined,
    };

    if (overwriteDependencyLocator) {
      await saveDependency?.({
        kind: 'replace',
        overwrittenLocator: overwriteDependencyLocator,
        newDependency,
      });
    } else {
      await saveDependency?.({
        kind: 'new',
        newDependency,
      });
    }

    onClose?.();
  };

  const testOrSaveButtonText = (shouldUseTestWorkflow && !hasTested) ? 'Test Dependency' : 'Save';
  const onTestOrSave = async () => {
    if (shouldUseTestWorkflow && !hasTested) {
      await onTest();
    } else {
      await onSave();
    }
  };

  return (
    <section>
      <header>
        <p className={styles.overview}>
          Search for an existing package and add it as a {isVendored ? 'vendored' : 'manual'} dependency to your project.
        </p>
      </header>

      <main className={styles.main}>
        <h3 className={styles.title}>
          Package Details
        </h3>
        {isVendored && (
          <div className={styles.field}>
            <LabeledTextInput
              id="search-dependency-file-path"
              size="small"
              label="File Path"
              placeholder="Path to the vendored dependency"
              value={filePath}
              onChange={(value) => {
                setFilePath(value);
                setHasTested(false);
              }}
              isDisabled={isTestingOrSaving}
              isRequired
            />
            <p className={styles.notes}>Add the path to the vendored dependency.</p>
          </div>
        )}

        <div className={styles.field}>
          <LabeledSelect
            id="search-dependency-package-manager"
            label="Package Manager"
            placeholder="Select package manager..."
            options={FETCHER_DROPDOWN_OPTIONS}
            value={FETCHER_DROPDOWN_OPTIONS.find((opt) => opt.value === packageManager)}
            onChange={(opt) => {
              setPackageManager(opt?.value);
              setHasTested(false);
            }}
            isDisabled={isTestingOrSaving}
            isRequired
          />
          <p className={styles.notes}>This tells FOSSA where to grab the same package for our analysis.</p>
        </div>

        <div className={styles.field}>
          <LabeledTextInput
            id="search-dependency-name"
            size="small"
            label="Name"
            placeholder="Enter a package name"
            value={name}
            onChange={(value) => {
              setName(value);
              setHasTested(false);
            }}
            isDisabled={isTestingOrSaving}
            isRequired
          />
        </div>

        <div className={styles.field}>
          <LabeledTextInput
            id="search-dependency-version"
            size="small"
            label="Version"
            placeholder="Enter a version number, eg. v1 or 1.0"
            value={version}
            onChange={(value) => {
              setVersion(value);
              setHasTested(false);
            }}
            isDisabled={isTestingOrSaving}
            isRequired
          />
        </div>
        {isSearchingForLinuxPackage && (
          <div className={styles.linuxFields}>
            <LabeledSelect
              id="search-dependency-distro"
              label="Distro"
              placeholder="Select distribution..."
              options={linuxDistrosByFetcher[packageManager]}
              value={linuxDistrosByFetcher[packageManager]?.find((opt) => opt.value === distro)}
              onChange={(opt) => {
                setDistro(opt?.value);
                setHasTested(false);
              }}
              isDisabled={isTestingOrSaving}
              isRequired
            />
            <LabeledTextInput
              id="search-dependency-release"
              size="small"
              label="Release"
              placeholder="24.04"
              value={release}
              onChange={(value) => {
                setRelease(value);
                setHasTested(false);
              }}
              isDisabled={isTestingOrSaving}
              isRequired
            />
            <LabeledSelect
              id="search-dependency-arch"
              label="Architecture"
              placeholder="Select architecture..."
              options={COMMON_LINUX_ARCH_OPTIONS}
              value={COMMON_LINUX_ARCH_OPTIONS.find((opt) => opt.value === arch)}
              onChange={(opt) => {
                setArch(opt?.value);
                setHasTested(false);
              }}
              isDisabled={isTestingOrSaving}
              isRequired
            />
          </div>
        )}
        {isCheckingRevision && <p>Testing whether this dependency can be resolved...</p>}
        {checkRevisionError && <p>There was an error checking this dependency. Please try again.</p>}
        {checkRevisionResponse && (
          <p>
            Locator: {checkRevisionResponse.locator} <br />
            Title: {checkRevisionResponse.title} <br />
            Homepage: {checkRevisionResponse.homepage} <br />
            Description: {checkRevisionResponse.description} <br />
            Release Date: {checkRevisionResponse.releaseDate} <br />
          </p>
        )}
      </main>

      <footer className={styles.footer}>
        <Button type="secondary" onClick={() => onClose?.()}>Cancel</Button>
        <LoaderButton
          type="primary"
          onClick={onTestOrSave}
          isDisabled={!canSave || isTestingOrSaving}
          isLoading={isTestingOrSaving}
        >
          {testOrSaveButtonText}
        </LoaderButton>
      </footer>
    </section>
  );
};

export default SearchDependency;
